import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ArrowRight, ArrowLeft, Sparkles, Target, MapPin, Users, ShieldCheck, Palette,
    Smartphone, Video, Megaphone, BarChart3, Rocket, HelpCircle, ExternalLink,
    Mail, Phone, Calendar, FileText, DollarSign, Globe, Layers, Zap, CheckCircle2, X,
    PlayCircle, TrendingUp, Building2, Hash,
} from "lucide-react";

/**
 * In-app pitch deck for FMO decision-makers.
 * URL params (all optional):
 *   ?fmo=Acme%20FMO            – org being pitched (defaults to generic "your FMO")
 *   ?presenter=Mark%20Stevens  – your name
 *   ?agent=mark-stevens        – slug used for "view live agent page" buttons
 */
export default function Pitch() {
    const [params] = useSearchParams();
    const fmo = params.get("fmo") || "your FMO";
    const presenter = params.get("presenter") || "Mark Stevens";
    const agentSlug = params.get("agent") || "mark-stevens";

    const [i, setI] = useState(0);
    const slides = useMemo(() => buildSlides({ fmo, presenter, agentSlug }), [fmo, presenter, agentSlug]);
    const total = slides.length;

    const go = useCallback((delta) => {
        setI((cur) => Math.max(0, Math.min(total - 1, cur + delta)));
    }, [total]);

    useEffect(() => {
        const handler = (e) => {
            if (["ArrowRight", "PageDown", " "].includes(e.key)) { e.preventDefault(); go(1); }
            else if (["ArrowLeft", "PageUp", "Backspace"].includes(e.key)) { e.preventDefault(); go(-1); }
            else if (e.key === "Home") setI(0);
            else if (e.key === "End") setI(total - 1);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [go, total]);

    const slide = slides[i];
    const progress = ((i + 1) / total) * 100;

    return (
        <div className="min-h-screen bg-[#0b1220] text-white relative overflow-hidden" data-testid="pitch-deck">
            {/* Top progress bar */}
            <div className="fixed top-0 left-0 right-0 h-1 bg-white/10 z-50">
                <div className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all" style={{ width: `${progress}%` }} />
            </div>

            {/* Subtle background pattern */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
                backgroundImage: "radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)",
                backgroundSize: "40px 40px",
            }} />

            {/* Slide */}
            <div key={i} className="relative z-10 min-h-screen flex flex-col px-6 sm:px-12 lg:px-20 py-14 animate-[fadeIn_.4s_ease-out]" data-testid={`pitch-slide-${i}`}>
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/50">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>{fmo === "your FMO" ? "FMO Partnership Pitch" : `${fmo} · Partnership Pitch`}</span>
                    </div>
                    <div className="text-xs uppercase tracking-[0.3em] text-white/50" data-testid="pitch-counter">{String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</div>
                </div>

                <div className="flex-1 flex items-center">
                    <div className="w-full max-w-6xl mx-auto">{slide}</div>
                </div>

                {/* Footer nav */}
                <div className="mt-10 flex items-center justify-between">
                    <button
                        onClick={() => go(-1)}
                        disabled={i === 0}
                        data-testid="pitch-prev-btn"
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1.5" /> Prev
                    </button>

                    <div className="hidden md:flex items-center gap-1.5">
                        {slides.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setI(idx)}
                                aria-label={`Go to slide ${idx + 1}`}
                                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-amber-400" : "w-2 bg-white/20 hover:bg-white/40"}`}
                            />
                        ))}
                    </div>

                    <button
                        onClick={() => go(1)}
                        disabled={i === total - 1}
                        data-testid="pitch-next-btn"
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-amber-400 text-slate-900 font-semibold hover:bg-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition"
                    >
                        Next <ArrowRight className="w-4 h-4 ml-1.5" />
                    </button>
                </div>
            </div>

            <Link to="/" className="fixed bottom-4 left-4 text-xs text-white/40 hover:text-white/80 z-50" data-testid="pitch-exit">
                ← Exit deck
            </Link>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}

// ----------------------------------------------------------------------
// Slide content
// ----------------------------------------------------------------------
function buildSlides({ fmo, presenter, agentSlug }) {
    return [
        // 1. COVER
        <Cover key="cover" />,

        // 2. THE OPPORTUNITY
        <Slide key="opp" eyebrow="The opportunity" title="Every annuity owner has questions. Almost nobody answers them." icon={<Target className="w-8 h-8" />}>
            <div className="grid md:grid-cols-3 gap-5 mt-10">
                <Stat big="50M+" label="Americans hold annuities" />
                <Stat big="78%" label="Don't fully understand fees, riders, or surrender terms" sub="LIMRA, 2024 retirement study" />
                <Stat big="<5%" label="Receive a written, independent review" />
            </div>
            <p className="mt-10 text-lg text-white/80 max-w-3xl">
                The carriers won't review their own products. The wirehouses don't want the conversation. That gap is our lead funnel.
            </p>
        </Slide>,

        // 3. WHAT I BUILT
        <Slide key="built" eyebrow="What I built" title="A lead-generation engine that ships agents pre-qualified, scored annuity reviews — fully white-labeled to the FMO." icon={<Sparkles className="w-8 h-8" />}>
            <div className="grid md:grid-cols-2 gap-6 mt-10">
                <FeatureCard icon={<FileText className="w-5 h-5" />} title="5-step Annuity Review Wizard" desc="Contact → annuity details → features → statement upload → submit. Generates a fees / flexibility / income scorecard." />
                <FeatureCard icon={<Calendar className="w-5 h-5" />} title="Calendly-powered booking" desc="Lead lands on agent's personal Calendly. Three-way call optional with FMO." />
                <FeatureCard icon={<MapPin className="w-5 h-5" />} title="ZIP + state lead routing" desc="Round-robin assignment by ZIP territory, then licensed state, then unassigned bucket. Zero manual triage." />
                <FeatureCard icon={<DollarSign className="w-5 h-5" />} title="Commission + override tracking" desc="Auto-calculated overrides on every sold lead. Built for FMO P&L." />
            </div>
        </Slide>,

        // 5. PRODUCT TOUR — Wizard
        <Slide key="wiz" eyebrow="The lead capture" title="A 5-step wizard that converts because it gives the visitor something — a real scorecard." icon={<Layers className="w-8 h-8" />}>
            <div className="grid md:grid-cols-5 gap-3 mt-10">
                {[
                    { n: 1, t: "Contact", d: "Name, phone, email, age, ZIP" },
                    { n: 2, t: "Annuity", d: "Type, carrier, value, year purchased" },
                    { n: 3, t: "Features", d: "Income rider? Surrender known?" },
                    { n: 4, t: "Statement", d: "Optional PDF upload" },
                    { n: 5, t: "Result", d: "Fees · Flexibility · Income score" },
                ].map((s) => (
                    <div key={s.n} className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="text-xs text-amber-300 font-semibold">STEP {s.n}</div>
                        <div className="mt-2 font-semibold text-white">{s.t}</div>
                        <div className="mt-1 text-xs text-white/60">{s.d}</div>
                    </div>
                ))}
            </div>
            <div className="mt-10 grid md:grid-cols-3 gap-4">
                <ScoreCard color="rose" label="Fees" score={62} note="Above-market mortality & expense" />
                <ScoreCard color="amber" label="Flexibility" score={48} note="Surrender 6 yrs remaining" />
                <ScoreCard color="emerald" label="Income strength" score={84} note="Strong rider, 6.5% rollup" />
            </div>
            <a href="/review?demo=1" target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center text-amber-300 hover:text-amber-200 text-sm" data-testid="pitch-link-review">
                Try the live wizard <ExternalLink className="w-4 h-4 ml-1.5" />
            </a>
        </Slide>,

        // 6. WHITE-LABEL
        <Slide key="wl" eyebrow="White-label, country-wide" title={`Drop-in your colors, logo, copy, and disclosures. ${fmo === "your FMO" ? "Your FMO" : fmo} owns the brand — I built the rails.`} icon={<Palette className="w-8 h-8" />}>
            <div className="grid lg:grid-cols-2 gap-10 mt-10 items-center">
                <div>
                    <ul className="space-y-3 text-white/80">
                        <Bullet><span className="font-semibold text-white">Live theme editor</span> — change CSS variables, every page updates instantly</Bullet>
                        <Bullet><span className="font-semibold text-white">Logo + tagline</span> uploadable from admin UI</Bullet>
                        <Bullet><span className="font-semibold text-white">Owner profile section</span> on the homepage (your photo, story, states)</Bullet>
                        <Bullet><span className="font-semibold text-white">Per-agent landing pages</span> — agents control their own bio, photo, Calendly</Bullet>
                        <Bullet><span className="font-semibold text-white">Multi-tenant ready</span> — same engine, different brand for each region/network if you want</Bullet>
                    </ul>
                    <a href="/admin/login?prefill=admin&next=/admin/branding" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center text-amber-300 hover:text-amber-200 text-sm" data-testid="pitch-link-branding">
                        See the live branding admin <ExternalLink className="w-4 h-4 ml-1.5" />
                    </a>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    {[
                        { name: fmo === "your FMO" ? "Region A" : fmo, c1: "#0b1220", c2: "#f59e0b" },
                        { name: "Region B", c1: "#1e3a8a", c2: "#22d3ee" },
                        { name: "Region C", c1: "#064e3b", c2: "#84cc16" },
                    ].map((b) => (
                        <div key={b.name} className="rounded-xl overflow-hidden border border-white/10">
                            <div className="h-24 flex items-end p-3" style={{ background: `linear-gradient(135deg, ${b.c1}, ${b.c2})` }}>
                                <div className="text-xs font-serif text-white drop-shadow truncate">{b.name}</div>
                            </div>
                            <div className="bg-white/5 px-3 py-2 text-[10px] text-white/60">Same engine · own brand</div>
                        </div>
                    ))}
                </div>
            </div>
        </Slide>,

        // 7. ROUTING
        <Slide key="routing" eyebrow="Lead routing" title="Every submission auto-assigned in milliseconds." icon={<Zap className="w-8 h-8" />}>
            <div className="mt-10 grid md:grid-cols-4 gap-3">
                {[
                    { step: "1", label: "Submitted via /agent/{slug}?", note: "→ that agent" },
                    { step: "2", label: "ZIP in agent territory?", note: "→ round-robin among matches" },
                    { step: "3", label: "State match (license)?", note: "→ round-robin among matches" },
                    { step: "4", label: "Unassigned bucket", note: "→ admin manual assign" },
                ].map((s) => (
                    <div key={s.step} className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="text-3xl font-serif text-amber-300">{s.step}</div>
                        <div className="mt-2 font-semibold text-white">{s.label}</div>
                        <div className="mt-1 text-sm text-white/60">{s.note}</div>
                    </div>
                ))}
            </div>
            <p className="mt-10 text-white/70 max-w-3xl">
                Round-robin balances by current open lead count — no agent gets buried, no agent goes hungry. You can override
                any assignment from the admin in one click.
            </p>
        </Slide>,

        // 8. AGENT EXPERIENCE
        <Slide key="agent" eyebrow="What agents get" title="A clean dashboard, a public profile they control, and a checklist that nudges them to finish." icon={<Users className="w-8 h-8" />}>
            <div className="grid lg:grid-cols-3 gap-5 mt-10">
                <FeatureCard icon={<BarChart3 className="w-5 h-5" />} title="Lead pipeline" desc="Status: New → Contacted → Appt Set → Sold → Closed. Notes per lead, statement download, click-to-call." />
                <FeatureCard icon={<Sparkles className="w-5 h-5" />} title="Self-service profile" desc="Photo, bio, Calendly, website, license states, ZIP territory, password — all editable." />
                <FeatureCard icon={<CheckCircle2 className="w-5 h-5" />} title="Profile completeness meter" desc="Live %% complete with missing-field checklist. Agents who fill it out convert noticeably more." />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
                <a href="/admin/login?prefill=agent" target="_blank" rel="noreferrer" className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-white/10 hover:bg-white/20" data-testid="pitch-link-login">
                    Open the agent dashboard live <ExternalLink className="w-4 h-4 ml-1.5" />
                </a>
                <span className="text-xs text-white/40 self-center">auto-signs in as Mark Stevens</span>
            </div>
        </Slide>,

        // 10. SOCIAL: TIKTOK
        <Slide key="tt" eyebrow="Demand engine #1 — TikTok / Reels" title="Short-form video is where 55+ retirees spend 70+ minutes a day in 2026." icon={<Video className="w-8 h-8" />}>
            <div className="grid lg:grid-cols-3 gap-5 mt-8">
                <PostMock platform="tiktok" handle={`@${(presenter || "annuity").toLowerCase().replace(/\s+/g, "")}`} hook={`"My grandma got SOLD a 12-year annuity at 78. Here's the line in the contract her agent didn't read."`} cta="Free written review · link in bio" stats="2.1M views · 38K likes · 4.8K saves" />
                <PostMock platform="tiktok" handle={`@${(presenter || "annuity").toLowerCase().replace(/\s+/g, "")}`} hook={`3 questions every annuity owner over 60 should ask their advisor today (most can't answer #2).`} cta="DM me ANNUITY for the checklist" stats="640K views · 9K shares" />
                <PostMock platform="tiktok" handle={`@${(presenter || "annuity").toLowerCase().replace(/\s+/g, "")}`} hook={`"Surrender charges aren't the trap. The income rider rollup is. Let me show you what your statement is hiding."`} cta="Free 3-way call with a licensed advisor" stats="1.3M views · 12K saves" />
            </div>
            <div className="mt-8 grid md:grid-cols-3 gap-4 text-sm">
                <Pill icon={<Hash className="w-4 h-4" />}>3 hooks/week, posted at 7-9pm CT</Pill>
                <Pill icon={<TrendingUp className="w-4 h-4" />}>Hashtags: #annuity #retirement #incomeplanning #fixedincome #seniorfinance</Pill>
                <Pill icon={<Megaphone className="w-4 h-4" />}>Bio link → /review form. Pinned video = "free review" CTA</Pill>
            </div>
        </Slide>,

        // 11. SOCIAL: FACEBOOK
        <Slide key="fb" eyebrow="Demand engine #2 — Facebook" title="55+ is still on Facebook every single day. Boosted local posts crush." icon={<Megaphone className="w-8 h-8" />}>
            <div className="grid lg:grid-cols-2 gap-6 mt-8">
                <PostMock platform="facebook" handle={`${presenter}, Licensed Insurance Agent`} hook={`"If you bought an annuity between 2015-2022, your contract probably has a feature you're not using. I'll review your statement free — no pressure, no obligation. Local Tulsa/OKC families only."`} cta="📞 Free 15-min call · click to book" stats="Boost: $25/day · 14k reach · 220 link clicks · 18 form fills (week 1)" />
                <PostMock platform="facebook" handle={`${presenter}, Licensed Insurance Agent`} hook={`"3 things your annuity statement is NOT telling you. (Save this post.) 1️⃣ How fees compound against your income rider 2️⃣ When your surrender clock actually expires 3️⃣ Why guaranteed ≠ best."`} cta={`Comment REVIEW — I'll DM the free checklist`} stats="Organic: 87 shares · 340 comments" />
            </div>
            <div className="mt-8 grid md:grid-cols-3 gap-4 text-sm">
                <Pill icon={<Target className="w-4 h-4" />}>Audience: 55-75, ZIPs in agent territory, interests = retirement/IRA/AARP</Pill>
                <Pill icon={<DollarSign className="w-4 h-4" />}>Boost budget: $20-50/day per local agent</Pill>
                <Pill icon={<PlayCircle className="w-4 h-4" />}>Pin a 60-second intro video on agent page</Pill>
            </div>
        </Slide>,

        // 15. CLOSE
        <Slide key="close" eyebrow="Thank you" title="Let's build the lead engine that the rest of the industry hasn't." icon={<Sparkles className="w-8 h-8" />}>
            <div className="mt-10 grid md:grid-cols-3 gap-5">
                <ContactCard icon={<Mail className="w-4 h-4" />} label="Email" value="mark@yourannuityreview.com" />
                <ContactCard icon={<Phone className="w-4 h-4" />} label="Phone" value="(405) 555-0101" />
                <ContactCard icon={<Globe className="w-4 h-4" />} label="Live demo" value="yourannuityreview.com" />
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
                <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center px-5 py-3 rounded-full bg-white text-slate-900 font-semibold hover:bg-white/90" data-testid="pitch-cta-home">
                    Open the live site <ExternalLink className="w-4 h-4 ml-2" />
                </a>
                <a href={`/agent/${agentSlug}`} target="_blank" rel="noreferrer" className="inline-flex items-center px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white">
                    My personal landing <ExternalLink className="w-4 h-4 ml-2" />
                </a>
                <a href="/admin/login?prefill=admin&next=/admin/branding" target="_blank" rel="noreferrer" className="inline-flex items-center px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white">
                    Try the white-label admin <ExternalLink className="w-4 h-4 ml-2" />
                </a>
            </div>
        </Slide>,
    ];
}

// ----------------------------------------------------------------------
// Slide primitives
// ----------------------------------------------------------------------
function Cover() {
    return (
        <div className="text-center" data-testid="pitch-cover">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-amber-300 mb-6">
                <Building2 className="w-4 h-4" /> Partnership pitch · for FMO leadership
            </div>
            <h1 className="font-serif text-5xl md:text-7xl leading-tight">
                A white-label lead engine<br />
                <span className="text-amber-300">for the next decade of FMOs.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
                Built and shipped by Strategic Media Holdings, LLC. A working multi-agent platform — already routing live leads.
            </p>
            <div className="mt-12 inline-flex items-center text-white/50 text-sm">
                <ArrowRight className="w-4 h-4 mr-2 animate-pulse" /> Press → / space to advance
            </div>
        </div>
    );
}

function Slide({ eyebrow, title, icon, children }) {
    return (
        <div>
            <div className="flex items-center gap-3 text-amber-300 mb-4">
                {icon}
                <span className="text-xs uppercase tracking-[0.3em]">{eyebrow}</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight max-w-4xl">{title}</h2>
            {children}
        </div>
    );
}

function Stat({ big, label, sub }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="font-serif text-5xl text-amber-300">{big}</div>
            <div className="mt-3 text-white">{label}</div>
            {sub && <div className="mt-1 text-xs text-white/50">{sub}</div>}
        </div>
    );
}

function FeatureCard({ icon, title, desc }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="w-9 h-9 rounded-md bg-amber-400/20 text-amber-300 flex items-center justify-center">{icon}</div>
            <div className="mt-3 font-semibold text-white">{title}</div>
            <div className="mt-1 text-sm text-white/60 leading-relaxed">{desc}</div>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            <span>{children}</span>
        </li>
    );
}

function BrowserMock({ url, children }) {
    return (
        <div className="rounded-xl overflow-hidden border border-white/15 shadow-2xl bg-slate-900">
            <div className="bg-slate-800/80 px-3 py-2 flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                <span className="ml-3 text-xs text-white/50 font-mono truncate">{url}</span>
            </div>
            <div>{children}</div>
        </div>
    );
}

function ScoreCard({ color, label, score, note }) {
    const colors = {
        rose: "from-rose-500 to-rose-700",
        amber: "from-amber-400 to-amber-600",
        emerald: "from-emerald-400 to-emerald-600",
    };
    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold">{label}</div>
            <div className="mt-3 flex items-end gap-2">
                <span className="font-serif text-4xl text-white">{score}</span>
                <span className="text-white/40 text-sm mb-1">/ 100</span>
            </div>
            <div className="mt-3 h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div className={`h-full bg-gradient-to-r ${colors[color]}`} style={{ width: `${score}%` }} />
            </div>
            <div className="mt-3 text-xs text-white/60">{note}</div>
        </div>
    );
}

function Row({ label, v, v2, highlight }) {
    return (
        <div className={`flex items-center justify-between ${highlight ? "text-amber-300" : "text-white/80"}`}>
            <span>{label}</span>
            <span className="font-semibold">
                {v} {v2 && <span className="text-xs text-white/50 ml-1">{v2}</span>}
            </span>
        </div>
    );
}

function PostMock({ platform, handle, hook, cta, stats }) {
    const tt = platform === "tiktok";
    return (
        <div className="bg-slate-900 border border-white/10 rounded-2xl overflow-hidden">
            <div className={`px-4 py-2 flex items-center gap-2 text-xs ${tt ? "bg-black" : "bg-[#1877F2]"}`}>
                {tt ? <Video className="w-3.5 h-3.5" /> : <Megaphone className="w-3.5 h-3.5" />}
                <span className="font-semibold">{platform === "tiktok" ? "TikTok" : "Facebook"}</span>
                <span className="text-white/60 ml-auto">{handle}</span>
            </div>
            <div className={`p-5 ${tt ? "bg-gradient-to-br from-slate-800 via-slate-900 to-black" : "bg-white text-slate-900"}`}>
                <div className={`font-serif text-lg leading-snug ${tt ? "text-white" : "text-slate-900"}`}>{hook}</div>
                <div className={`mt-3 inline-block text-xs px-2 py-1 rounded-full ${tt ? "bg-amber-400 text-slate-900" : "bg-blue-600 text-white"}`}>{cta}</div>
            </div>
            <div className="px-4 py-2 text-[11px] text-white/50 border-t border-white/10">{stats}</div>
        </div>
    );
}

function Pill({ icon, children }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-full px-4 py-2 inline-flex items-center gap-2 text-white/80">
            <span className="text-amber-300">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function PhaseCard({ badge, title, bullets, accent }) {
    return (
        <div className={`rounded-2xl p-6 border ${accent ? "bg-amber-400/10 border-amber-400/30" : "bg-white/5 border-white/10"}`}>
            <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${accent ? "text-amber-300" : "text-white/50"}`}>{badge}</div>
            <div className="mt-2 font-serif text-2xl text-white">{title}</div>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
                {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                        <span className={`w-1 h-1 rounded-full mt-2 shrink-0 ${accent ? "bg-amber-300" : "bg-white/40"}`} /> {b}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ContactCard({ icon, label, value }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-[0.2em] font-semibold">
                {icon} {label}
            </div>
            <div className="mt-2 font-serif text-xl text-white break-all">{value}</div>
        </div>
    );
}
